import { Zap } from "lucide-react";
import { useEffect, useState } from "react";

interface XPToastData {
  amount: number;
  reason?: string;
}

interface XPToastProps {
  data: XPToastData;
  visible: boolean;
  onDone: () => void;
}

const XPToast = ({ data, visible, onDone }: XPToastProps) => {
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(onDone, 2500);
      return () => clearTimeout(timer);
    }
  }, [visible, onDone]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[100] pointer-events-none animate-scale-in">
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 text-white shadow-lg shadow-amber-500/30 font-bold text-sm">
        <Zap className="w-4 h-4 fill-current" />
        <span>+{data.amount} XP{data.reason ? ` ${data.reason}` : ""}</span>
      </div>
    </div>
  );
};

// Global XP toast state
let showXPToastFn: ((amount: number, reason?: string) => void) | null = null;

export const triggerXPToast = (amount: number, reason?: string) => {
  showXPToastFn?.(amount, reason);
};

export const XPToastProvider = () => {
  const [queue, setQueue] = useState<XPToastData[]>([]);
  const [current, setCurrent] = useState<XPToastData | null>(null);

  useEffect(() => {
    showXPToastFn = (amount: number, reason?: string) => {
      setQueue(prev => [...prev, { amount, reason }]);
    };
    return () => { showXPToastFn = null; };
  }, []);

  useEffect(() => {
    if (current === null && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue(prev => prev.slice(1));
    }
  }, [queue, current]);

  return (
    <XPToast
      data={current ?? { amount: 0 }}
      visible={current !== null}
      onDone={() => setCurrent(null)}
    />
  );
};

export default XPToast;
